/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Form } from "react-bootstrap";
import envConfig from "../config/envConfig";
import APIHelper from "../api/APIHelper";
import DynamicTable from "../components/applications/DynamicTableComponent";
import Paginator from "../components/common/Paginator";
import ProgressBarDialog from "../components/common/ProgressBar";
import AlertDialog from "../components/common/AlertDialog";
import { useTheme } from "../theme/ThemeContext";
import { retrieve } from "../utils/storage_helper";
import ContentDisplayModal from "../components/common/ContentDisplayModal";
import Button from "../components/Button";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const DEFAULT_PAGE_SIZE = 20;

const TASK_NAME = "Export of Table Data";

const STATUS_CHOICES = [
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'partially_completed', label: 'Partially Completed' },
    { value: 'failed', label: 'Failed' },
];

const EXPORT_TABLE_SCHEMA = {
    fields: [
        { name: 'task_name', display_name: 'Task Name', data_type: 'string' },
        { name: 'status', display_name: 'Status', data_type: 'string' },
        { name: 'created_at', display_name: 'Created At', data_type: 'datetime' },
        { name: 'updated_at', display_name: 'Updated At', data_type: 'datetime' },
        { name: 'metadata', display_name: 'Download Link', data_type: 'string' },
        { name: 'logs', display_name: 'Logs', data_type: 'string' }
    ]
};

export default function ExportsPage() {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(DEFAULT_PAGE_SIZE);
    const [statusFilter, setStatusFilter] = useState("");

    // State for viewing logs in modal
    const [logContent, setLogContent] = useState(null);
    const [isLogModalOpen, setLogModalOpen] = useState(false);
    const [downloadError, setDownloadError] = useState(null);

    const organizationId = envConfig.organizationId;
    const applicationId = envConfig.applicationId;
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const queryKey = useMemo(
        () => ["exportsData", { page, pageSize, statusFilter, organizationId, applicationId }],
        [page, pageSize, statusFilter, organizationId, applicationId]
    );

    const fetchExportsFn = async ({ signal }) => {
        let url = `/arcee/application/jobs/list/?page=${page}&page_size=${pageSize}&organization_id=${organizationId}&application_id=${applicationId}&task_name=${encodeURIComponent(TASK_NAME)}`;

        if (statusFilter) {
            url += `&status=${encodeURIComponent(statusFilter)}`;
        }

        const resp = await apiHelper.apiCall(url, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            signal
        });

        if (!resp.ok) {
            let errText = `Failed to fetch exports (${resp.status})`;
            try {
                const j = await resp.json();
                if (j && j.error) errText = j.error;
            } catch (e) { /* ignore */ }
            throw new Error(errText);
        }
        return await resp.json();
    };

    const {
        data,
        error,
        isLoading,
        isFetching,
        refetch
    } = useQuery({
        queryKey: queryKey,
        queryFn: fetchExportsFn,
        keepPreviousData: true,
        staleTime: 10000,
        retry: 1
    });

    const records = data ? data.results || data.records || [] : [];
    const totalRecords = data ? data.count || 0 : 0;

    const handleDownload = async (record) => {
        try {
            // Assume table_name is in metadata
            let meta = {};
            try {
                if (typeof record.metadata === 'string') {
                    meta = JSON.parse(record.metadata);
                } else if (typeof record.metadata === 'object') {
                    meta = record.metadata || {};
                }
            } catch (e) {
                console.error("Failed to parse metadata", e);
            }

            const tableName = meta.table_name;
            var filename = meta.filename;

            if (!filename) {
                const relativePath = meta.output_relative_path;

                if (!relativePath) {
                    throw new Error("No file path found in export record.");
                }

                const parts = relativePath.split(/[/\\]/);
                filename = parts.length > 0 ? parts[parts.length - 1] : "download.file";
            }//if

            if (!tableName) {
                throw new Error("Missing table name in export record.");
            }

            const response = await apiHelper.apiCall("/arcee/application/exports/download/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    table_name: tableName,
                    organization_id: organizationId,
                    application_id: applicationId,
                    filename: filename,
                }),
            });

            if (!response.ok) {
                let errText = `Failed to download file (${response.status})`;
                try {
                    const j = await response.json();
                    if (j && j.error && j.error.message) errText = j.error.message;
                } catch (e) { /* ignore */ }
                throw new Error(errText);
            }

            // Create a blob from the response
            const blob = await response.blob();
            // Create a link to download it
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Prevent Chrome "File wasn't available on site" error by delaying Blob destruction
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);

        } catch (err) {
            setDownloadError(err.message);
        }
    };

    const processedRecords = useMemo(() => {
        return records.map(record => {
            let meta = {};
            try {
                if (typeof record.metadata === 'string') {
                    meta = JSON.parse(record.metadata);
                } else if (typeof record.metadata === 'object') {
                    meta = record.metadata || {};
                }
            } catch (e) {
                console.error("Failed to parse metadata", e);
            }

            const tableName = meta.table_name || '-';
            const relativePath = meta.output_relative_path;
            const filename = meta.filename || tableName;

            let tableNameNode = tableName;
            if (relativePath && tableName !== '-') {
                tableNameNode = (
                    <span
                        onClick={() => handleDownload(record)}
                        style={{
                            color: theme.primary,
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 500
                        }}
                    >
                        {filename}
                    </span>
                );
            }

            const metadataNode = (
                <div>
                    <div>{tableNameNode}</div>
                </div>
            );

            let logsNode = "-";
            if (record.logs) {
                logsNode = (
                    <button
                        onClick={() => {
                            const formatted = record.logs.replace(/\|/g, '\n');
                            setLogContent(formatted);
                            setLogModalOpen(true);
                        }}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: theme.primary, // Using theme primary color
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontSize: "14px",
                            padding: 0
                        }}
                    >
                        View Logs
                    </button>
                );
            }

            return {
                ...record,
                metadata: metadataNode,
                logs: logsNode
            };
        });
    }, [records, theme]);

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(1);
    };

    const clearStatus = () => {
        setStatusFilter("");
        setPage(1);
    };

    return (
        <div style={{ overflowX: "hidden" }}>
            <div className="container mt-4" style={{ maxWidth: 3000, margin: "0 auto" }}>
                <ProgressBarDialog
                    open={isLoading || isFetching}
                    text_message="Loading exports..."
                />

                <AlertDialog
                    open={!!error || !!downloadError}
                    title="Error"
                    message={error?.message || error || downloadError}
                    type="error"
                    okLabel="Close"
                    showCancel={false}
                    onOk={() => {
                        if (downloadError) {
                            setDownloadError(null);
                        } else {
                            navigate('/dashboard');
                        }
                    }}
                />

                <div style={{ marginBottom: 20 }}>
                    <h3 style={{ margin: "0 0 16px 0", fontWeight: 700, color: '#1a1f36' }}>
                        Exports
                    </h3>

                    {/* Custom Toolbar for Exports */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            background: "#f8f9fc",
                            padding: "12px",
                            borderRadius: theme.cardRadius,
                            border: "1px solid #e1e4e8"
                        }}
                    >
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: windowWidth <= 615 ? "wrap" : "nowrap",
                            width: windowWidth <= 615 ? "100%" : "auto"
                        }}>
                            <Form.Select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                style={{
                                    borderRadius: theme.buttonRadius,
                                    border: "1px solid #d1d5db",
                                    minWidth: 200,
                                    fontSize: "14px",
                                    width: windowWidth <= 615 ? "100%" : "auto"
                                }}
                            >
                                <option value="">Filter by Status...</option>
                                {STATUS_CHOICES.map(choice => (
                                    <option key={choice.value} value={choice.value}>
                                        {choice.label}
                                    </option>
                                ))}
                            </Form.Select>

                            {statusFilter && (
                                <Button mode="outlined" onClick={clearStatus}>
                                    <i className="bi bi-x" style={{ fontSize: 16, marginRight: 2 }} />
                                    Clear
                                </Button>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button mode="bordered" onClick={() => refetch()} aria-label="Refresh">
                                <i className="bi bi-arrow-clockwise" style={{ fontSize: 16, marginRight: 4 }} />
                                Refresh
                            </Button>
                        </div>

                    </div>
                </div>

                {!isLoading && !error && (
                    <>
                        <DynamicTable
                            tableSchema={EXPORT_TABLE_SCHEMA}
                            records={processedRecords}
                            page={page}
                            pageSize={pageSize}
                            mode="simple"
                        />

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: 12
                            }}
                        >
                            {Math.ceil(totalRecords / pageSize) > 1 && totalRecords > 0 && (
                                <div
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 14,
                                        minWidth: 200
                                    }}
                                >
                                    Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalRecords)} of {totalRecords} records
                                </div>
                            )}

                            <Paginator
                                page={page}
                                pageCount={Math.max(1, Math.ceil(totalRecords / pageSize))}
                                onPrevious={(p) => setPage(p)}
                                onPage={(p) => setPage(p)}
                                onNext={(p) => setPage(p)}
                                maxPagesToShow={5}
                            />
                        </div>
                    </>
                )}
                <ContentDisplayModal
                    open={isLogModalOpen}
                    title="Export Logs"
                    onClose={() => setLogModalOpen(false)}
                >
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                        {logContent}
                    </pre>
                </ContentDisplayModal>

            </div>
        </div>
    );
}
